import React, { useEffect, useState } from 'react';
import Label from './Label/Label';
import { getTaxIdType } from '../../../lib/tax-id';
import { debounce } from 'lodash';
import useCheckoutState from '../../../hooks/useCheckoutState';
import TextInput from '../../Form/components/TextInput/TextInput';

function validateTaxIdType(taxId, country) {
  if (!country)
    return {
      taxId: taxId,
      taxIdType: null,
      valid: true,
    };

  const result = getTaxIdType(taxId, country);

  if (result.type) {
    return {
      taxId: result.taxId,
      taxIdType: result.type,
      valid: true,
    };
  }

  if (result.error) {
    if (result.expectedFormats) {
      if (result.expectedFormats.length === 1) {
        throw new Error(`Use this format: ${result.expectedFormats[0]}`);
      }

      const expectedFormats = result.expectedFormats.join(', ');

      throw new Error(`Use one of these formats: ${expectedFormats}`);
    }
  }

  return {
    taxId: result.taxId,
    taxIdType: result.type,
    valid: result.type !== null,
  };
}

export default function TaxIdField({ field, onChange, onBlur }) {
  const {
    form: { fields = {} },
  } = useCheckoutState();
  const { label } = field;
  const [validationError, setValidationError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  const shippingAddressCountry = Object.values(fields).find(
    field => field.type === 'shipping-address-country',
  )?.value;
  const billingAddressCountry = Object.values(fields).find(
    field => field.type === 'address-country',
  )?.value;
  const country = shippingAddressCountry || billingAddressCountry;

  // debounce validation
  const debouncedValidate = React.useCallback(
    debounce((taxId, country) => {
      try {
        const { taxIdType } = validateTaxIdType(taxId, country);
        setValidationError(null);

        // store taxIdType in metadata
        onChange(taxId, { type: taxIdType });
      } catch (error) {
        setValidationError(error.message);
      }
    }, 500),
    [],
  );

  useEffect(() => {
    if (!country) return;
    if (!field.value) return;

    debouncedValidate(field.value, country);
  }, [field.value, country, isFocused]);

  // we don't actually validate the tax id here, we just make sure it's the correct type
  // for some countries, that means we need to validate the format of the tax id
  // for others, we just grab the type from the country

  return (
    <>
      {label && (
        <Label htmlFor={field._id} className="cp_form-label" required={field.required}>
          {label}
        </Label>
      )}

      <TextInput
        onChange={e => {
          onChange(e.target.value);
        }}
        key={field._id}
        required={field.required}
        value={field.value}
        placeholder={field.placeholder || label}
        className="cp_form-textinput"
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          onBlur();
        }}
      />

      {validationError && (
        <p className="cp_form-error mt-1 text-sm text-red-500">{validationError}</p>
      )}
    </>
  );
}
